import { BumpChart, LineChart, StackedAreaChart } from "@get-dx/d3-charts";
import {
  getQueryStringParams,
  setQueryStringParam,
  deleteQueryStringParam,
  objectToQueryString,
  generatePreviousComparisonValues,
  generateBenchmarkComparisonValues,
  arrayRange,
  sortFunc,
  comparisonOptionsMap,
  snapshotSquadsFromCache,
  generateOrgComparisonValues,
  numberToSpelling,
} from "../../shared/utils";
import { chartTooltipHtml } from "../chartTooltipHtml";
import { teamTreeExpansionMixin } from "../mixins/teamTreeExpansion";
import { peopleFiltersMixin } from "../mixins/peopleFilters";
import { pushNotification } from "../pushNotification";
import { scrubHtml, tooltipIfTruncated } from "../../lib/utils";
import { getDistributionColors } from "../chartColors";

const targetPositionCountMap = new Map([
  [0, "zero_position_count"],
  [1, "one_position_count"],
  [2, "two_position_count"],
  [3, "three_position_count"],
  [4, "four_position_count"],
  [5, "five_position_count"],
  [6, "six_position_count"],
]);

const benchmarkDiffConversions = new Map([
  ["vs_prev", "prev_percentage"],
  ["vs_org", "vs_org"],
  ["vs_50th", "benchmark_50"],
  ["vs_75th", "benchmark_75"],
  ["vs_90th", "benchmark_90"],
]);

const COMMENT_PAGE_SIZE = 25;

function generateComparisonValues(
  resource,
  benchmark,
  resourceType = "factor",
) {
  let comparisonValues = [];

  if (
    ["vs_prev", "prev_percentage"].includes(benchmark) &&
    resource.values.length > 1
  ) {
    comparisonValues = generatePreviousComparisonValues(resource);
  } else if (
    !["vs_prev", "prev_percentage"].includes(benchmark) &&
    benchmark !== "vs_org"
  ) {
    comparisonValues = generateBenchmarkComparisonValues(
      resource,
      resourceType,
      benchmark,
    );
  }

  return comparisonValues;
}

export function snapshotSentiment(defaults) {
  const {
    baseUrl,
    dxiDrilldownTabs,
    minResponseThreshold,
    drilldownTabs,
    maxBehavioralQuestions,
    tagOptions,
    groups,
    hasPreviousSnapshot,
    snapshotId,
    showFullIntro,
    exportCommentsUrl,
    isLeafDri,
    activeBranch,
    showBranchingCallout,
    queryParams,
  } = defaults;

  const targetedFactor = getQueryStringParams().get("factor_id");
  const dxiTargeted = getQueryStringParams().get("dxi");

  const { currentUser } = window;

  const drilldownTab = getQueryStringParams().get("ddt");
  // Ensure valid drilldown tab is set
  if (
    drilldownTab &&
    !drilldownTabs.some((t) => t.tab == drilldownTab) &&
    !dxiDrilldownTabs.some((t) => t.tab == drilldownTab)
  ) {
    setQueryStringParam("ddt", "overview");
  }

  return {
    // Enable shared tree expansion behavior
    ...teamTreeExpansionMixin(),
    // Enable shared people filtering behavior
    ...peopleFiltersMixin(),
    // Configure id mapping for sentiment rows and cached squads
    rowIdKey: "snapshot_squad_id",
    cachedParentKey: "parent_id",

    // Getters for squad, group, and tag IDs from store
    // Always prefer store values over URL parameters for consistency
    get squadIds() {
      return this.$store?.snapshotResultFilters?.squadIds || [];
    },

    get groupIds() {
      return this.$store?.snapshotResultFilters?.groupIds || [];
    },

    get tagIds() {
      return this.$store?.snapshotResultFilters?.tagIds || [];
    },
    tooltipIfTruncated,
    dxiDrilldownTabs,
    groups,
    allComments: [],
    commentIds: new Set(),
    commentSearchTerm: "",
    drilldownCommentSearchTerm: "",
    commentSearchResults: [],
    drilldownCommentSearchResults: [],
    commentSummaries: {},
    commentSummaryGenerating: false,
    minResponseThreshold,
    commentsLoaded: false,
    searchCommentsLoaded: false,
    drilldownSearchCommentsLoaded: false,
    hasMoreComments: false,
    commentPage: 0,
    resultItemsLoaded: false,
    resultItems: [],
    responseCount: null,
    recipientCount: null,
    drilldownModalOpen: !!targetedFactor,
    targetedFactor,
    trends: null,
    xStart: null,
    maxBehavioralQuestions,
    showBranchingCallout,
    xEnd: null,
    dxiScore: null,
    benchmarkableDxiScore: null,
    dxiModalOpen: !!dxiTargeted,
    orgDxiScore: null,
    dxi50: null,
    dxi75: null,
    dxi90: null,
    tableDataLoaded: false,
    isFetchingTableData: false,
    tabDataLoaded: false,
    selectedDrilldownTab: getQueryStringParams().get("ddt"),
    previousDxiScore: null,
    drilldownData: {},
    maxTeamPriority: 0,
    selectedFactorRankChange: null,
    maxTeamScoreForDriver: 0,
    squadNamesFromSquadIds: [],
    showTeamBreakdown: true,
    showCommentsSearch: false,
    viewAllDrivers: false,
    exportCommentsUrl,
    commentVisibilitySetting: "all",
    hasTeamOnlyComments: false,
    hasAdminOnlyComments: false,
    hasGlobalComments: false,
    isLeafDri: isLeafDri || false,
    activeBranch,
    init() {
      // Reset store values from current URL params when component initializes
      // This fixes the issue where Turbo navigation retains old store values
      const currentParams = getQueryStringParams();
      const squadIdsFromUrl =
        currentParams
          .get("squad_ids")
          ?.split(",")
          .filter(Boolean)
          .map(Number) || [];
      const groupIdsFromUrl =
        currentParams
          .get("group_ids")
          ?.split(",")
          .filter(Boolean)
          .map(Number) || [];

      // Ensure URL-driven filters are mutually exclusive and override any stale store state
      if (groupIdsFromUrl.length > 0) {
        // Groups present in URL: clear squads and set filterType accordingly
        this.$store.snapshotResultFilters.squadIds = [];
        this.$store.snapshotResultFilters.groupIds = groupIdsFromUrl;
        this.$store.snapshotResultFilters.filterType = "groups";
      } else {
        // Squads (teams) present in URL (or neither): clear groups and set filterType accordingly
        this.$store.snapshotResultFilters.squadIds = squadIdsFromUrl;
        this.$store.snapshotResultFilters.groupIds = [];
        this.$store.snapshotResultFilters.filterType = "teams";
      }

      this.$store.snapshotResultFilters.tagIds =
        currentParams.get("tag_ids")?.split(",").filter(Boolean).map(Number) ||
        [];

      this.$root.addEventListener("dx:squadsTree:init", () => {
        this.determineSquadNamesFromSquadIds();
      });

      if (this.activeBranch) {
        this.$store.snapshotResultFilters.branch = this.activeBranch;
      }

      if (queryParams.squad_ids) {
        this.$store.snapshotResultFilters.squadIds = queryParams.squad_ids
          .split(",")
          .filter(Boolean)
          .map(Number);
      }
      if (queryParams.tag_ids) {
        this.$store.snapshotResultFilters.tagIds = queryParams.tag_ids
          .split(",")
          .filter(Boolean)
          .map(Number);
      }
      if (queryParams.group_ids) {
        this.$store.snapshotResultFilters.groupIds = queryParams.group_ids
          .split(",")
          .filter(Boolean)
          .map(Number);
      }

      this.fetchAll().then(() => {
        // Default this page to sort by priority if not specified in URL
        if (!getQueryStringParams().get("sb")) {
          if (this.isLeafDri) {
            this.$store.snapshotResultFilters.sortBy = "priority";
            this.$store.snapshotResultFilters.sortReverse = false;
          } else {
            this.$store.snapshotResultFilters.sortBy = "comparison";
            this.$store.snapshotResultFilters.sortReverse = false;
          }
        }

        if (this.selectedDrilldownTab) {
          if (dxiTargeted) {
            const selectedItem = dxiDrilldownTabs.find(
              (t) => t.tab == this.selectedDrilldownTab,
            );
            this.fetchTab(this.selectedDrilldownTab, selectedItem.url, "dxi");
          } else {
            const selectedItem = drilldownTabs.find(
              (t) => t.tab == this.selectedDrilldownTab,
            );
            this.fetchTab(
              this.selectedDrilldownTab,
              selectedItem.url,
              "driver",
            );
          }
        }

        if (showFullIntro || showBranchingCallout) {
          setTimeout(() => {
            let steps = [];

            if (showBranchingCallout) {
              steps.push({
                element: document.querySelector(".x-snapshot-branches"),
                intro: "Switch snapshot branches.",
              });
            }

            if (showFullIntro) {
              steps = [
                ...steps,
                {
                  element: document.querySelector(".x-team-filter"),
                  intro: "Filter results by team.",
                },
                {
                  element: document.querySelector(".x-driver"),
                  intro: "Click on a row to view more details.",
                },
              ];
            }

            window
              .introJs()
              .setOptions({
                exitOnEsc: false,
                exitOnOverlayClick: false,
                doneLabel: "Got it",
                scrollToElement: false,
                disableInteraction: true,
                steps,
              })
              .start()
              .oncomplete(() => {
                window.ack(showFullIntro);
                if (showBranchingCallout) {
                  // Delay so a race condition doesn't cause one of the acks to nullify the other
                  setTimeout(() => {
                    window.ack(showBranchingCallout);
                  }, 1000);
                }
              });
          }, 50);
        }
      });

      this.$watch("$store.snapshotResultFilters.compareTo", () => {
        this.handleComparisonChange();
      });
      this.$watch("$store.snapshotResultFilters.previousSnapshotId", () => {
        this.handleComparisonChange();
      });
    },
    get tagNamesFromTagIds() {
      const selectedTags = tagOptions.filter(
        (tag) =>
          this.tagIds.includes(String(tag.id)) || this.tagIds.includes(tag.id),
      );

      return selectedTags.map((tag) => tag.name);
    },
    get visibleDrilldownTabs() {
      let filteredTabs = drilldownTabs.filter((drilldownTab) => {
        if (drilldownTab.tab == "team_breakdown") {
          return this.showTeamBreakdown && !this.isFilteringByGroups();
        }
        if (drilldownTab.tab == "priority") return hasPreviousSnapshot;
        // Related workflows requires at least one workflow
        if (drilldownTab.tab == "related_workflows")
          return this.selectedFactor()?.has_workflows;
        // Recommendations requires an article
        if (drilldownTab.tab == "recommendations")
          return this.selectedFactor()?.atlas_article_id;

        return true;
      });

      if (
        this.responseCount < minResponseThreshold &&
        this.selectedFactor()?.comment_count > 0
      ) {
        filteredTabs = filteredTabs.filter((t) => t.tab == "comments");
      } else if (
        this.responseCount < minResponseThreshold &&
        this.selectedFactor()?.comment_count == 0
      ) {
        // NOTE: This case technically shouldn't happen because the modal is prevented from opening if the conditions aren't met.
        // But with the change in logic in the sentiment/overview partial, this is a good safeguard.
        filteredTabs = [];
      }

      return filteredTabs;
    },
    get visibleDxiDrilldownTabs() {
      let filteredTabs = this.dxiDrilldownTabs;

      // Hide only the Breakdowns tab when filtering by groups
      if (this.groupIds.length > 0) {
        filteredTabs = filteredTabs.filter(
          (tab) => tab.tab !== "team_breakdown",
        );
      }

      return filteredTabs;
    },
    get cachedSquads() {
      return snapshotSquadsFromCache(snapshotId);
    },
    get showOrgComparison() {
      return this.squadIds.length > 0;
    },
    get showScores() {
      return this.responseCount >= minResponseThreshold;
    },
    get compareTo() {
      return this.$store.snapshotResultFilters.compareTo;
    },
    get previousSnapshotId() {
      return this.$store.snapshotResultFilters.previousSnapshotId;
    },
    get sortReverse() {
      return this.$store.snapshotResultFilters.sortReverse;
    },
    get sortBy() {
      return this.$store.snapshotResultFilters.sortBy;
    },
    get drilldownSortBy() {
      return this.$store.snapshotResultFilters.drilldownSortBy;
    },
    get drilldownSortReverse() {
      return this.$store.snapshotResultFilters.drilldownSortReverse;
    },
    get driverComments() {
      // For the comments in the drilldown modal
      if (
        this.selectedDrilldownTab === "comments" &&
        this.drilldownCommentSearchResults.length > 0
      ) {
        return this.drilldownCommentSearchResults;
      } else if (
        this.selectedDrilldownTab === "comments" &&
        this.drilldownCommentSearchResults.length === 0
      ) {
        return this.filteredComments || [];
      }

      // For the comments in the sidebar
      if (this.commentSearchResults.length > 0) {
        return this.commentSearchResults;
      }

      return this.filteredComments || [];
    },
    get responseCompletionPercentage() {
      if (!this.recipientCount) return `0 of 0 responded`;

      const percentage = Math.round(
        (this.responseCount * 100.0) / this.recipientCount,
      );

      return `${this.responseCount} of ${this.recipientCount} responded (${percentage}%)`;
    },
    get hasMultipleTrendValues() {
      const validValues = this?.trends?.values?.filter(
        (v) => v.value !== null && v.value !== undefined && !isNaN(v.value),
      );
      return validValues?.length >= 2;
    },
    get noSearchResults() {
      if (this.selectedDrilldownTab === "comments") {
        return (
          this.drilldownCommentSearchResults.length == 0 &&
          this.drilldownCommentSearchTerm &&
          this.drilldownSearchCommentsLoaded
        );
      } else {
        return (
          this.commentSearchResults.length == 0 &&
          this.commentSearchTerm &&
          this.searchCommentsLoaded
        );
      }
    },
    get dxiComparisonValue() {
      // If you update logic here, be sure to update get_dxi_benchmarks in app/services/reports/core4/dxi.rb
      // which is used in all other areaas of the app (reports, dashboard, benchmark report builder, etc)

      // Calculate vs_prev and vs_org comparisons before verifying that we have a benchmarkable DXI score
      // Comparing to a previous DXI score or org DXI score does not require a benchmarkable DXI score
      // Checking for benchmarkable DXI score first will cause custom drivers to be excluded from the comparison and display N/A
      if (this.compareTo == "vs_prev") {
        return this.previousDxiScore != null && this.dxiScore != null
          ? this.dxiScore - this.previousDxiScore
          : null;
      } else if (this.compareTo == "vs_org") {
        return this.dxiScore != null && this.orgDxiScore != null
          ? this.dxiScore - this.orgDxiScore
          : null;
      }

      // Benchmark-based comparisons require a benchmarkable DXI
      if (
        this.benchmarkableDxiScore == null ||
        isNaN(this.benchmarkableDxiScore)
      )
        return null;

      if (this.compareTo == "vs_50th") {
        return this.benchmarkableDxiScore - this.dxi50;
      } else if (this.compareTo == "vs_75th") {
        return this.benchmarkableDxiScore - this.dxi75;
      } else if (this.compareTo == "vs_90th") {
        return this.benchmarkableDxiScore - this.dxi90;
      }
      return null;
    },
    calculateDxiBenchmark(benchmarkComp) {
      // If you update logic here, be sure to update get_dxi_benchmarks in app/services/reports/core4/dxi.rb
      // which is used in all other areaas of the app (reports, dashboard, benchmark report builder, etc)

      const benchmarkAttribute = benchmarkDiffConversions.get(benchmarkComp);
      const relevantItems = this.driverItems.filter(
        (item) => item[benchmarkAttribute],
      );
      const totalScore = relevantItems.reduce(
        (sum, obj) => sum + obj[benchmarkAttribute],
        0,
      );
      return Math.round(totalScore / relevantItems.length);
    },
    selectedFactorScore() {
      return this.selectedFactor()?.score;
    },
    get selectedFactorIs5Point() {
      return this.selectedFactor()?.scale_points == 5;
    },
    get selectedFactorIs3Point() {
      return this.selectedFactor()?.scale_points == 3;
    },
    selectedFactorOption(position) {
      let label;

      if (this.selectedFactorIs3Point) {
        if (position == 1) label = "Bad";
        if (position == 2) label = "So-so";
        if (position == 3) label = "Good";
      }

      if (this.selectedFactorIs5Point) {
        if (position == 1) label = "Never";
        if (position == 2) label = "Rarely";
        if (position == 3) label = "Sometimes";
      }

      return label;
    },
    selectedFactor() {
      if (this.targetedFactor == null) return "";

      return this.driverItems.find((row) => row.id == this.targetedFactor);
    },
    selectedFactorPrompt() {
      return this.selectedFactor()?.prompt;
    },
    get selectedFactorSummary() {
      return this.commentSummaries[this.targetedFactor]?.summary;
    },
    get selectedFactorRecommendations() {
      return this.commentSummaries[this.targetedFactor]?.recommendations;
    },
    get selectedFactorRecommendationsHtml() {
      return this.commentSummaries[this.targetedFactor]?.recommendations_html;
    },
    getFactorSummary(factorId) {
      // Returns just the summary text for use in tooltips
      return this.commentSummaries?.[factorId]?.summary;
    },
    generateCommentSummary() {
      this.commentSummaryGenerating = true;

      const fullUrl = `${baseUrl}/comments/generate_summary?${this.queryParams()}`;

      return fetch(fullUrl)
        .then((resp) => resp.json())
        .then((data) => {
          // Optimistically update the selected factor summary
          this.commentSummaries[this.targetedFactor] = data;
        })
        .catch((error) => {
          console.error("Error generating summary items", error);
        })
        .finally(() => {
          this.commentSummaryGenerating = false;
        });
    },
    percentageOfResponsesOverview(positionCount, totalFieldCount) {
      const item = this.selectedFactor();

      if (!item) return;

      if (item[totalFieldCount] === 0) return "0%";

      return `${Math.round(
        (100 * item[positionCount]) / item[totalFieldCount],
      )}%`;
    },
    percentageOfResponses(item, positionCount) {
      if (item.count === 0) return "0%";

      return `${Math.round((100 * item[positionCount]) / item.count)}%`;
    },
    sentimentDistributionItems() {
      const item = this.selectedFactor();
      if (!item) return [];

      const countFields = this.selectedFactorIs3Point
        ? ["one_count", "two_count", "three_count"]
        : ["one_count", "two_count", "three_count", "four_count", "five_count"];

      const labels = this.selectedFactorIs3Point
        ? ["Bad", "So-so", "Good"]
        : ["Never", "Rarely", "Sometimes", "Very often", "Always"];

      return countFields
        .map((field, index) => {
          const count = item[field] || 0;
          const percentage =
            item.count > 0 ? Math.round((100 * count) / item.count) : 0;

          return {
            label: labels[index],
            count: count,
            percentage: `${percentage}%`,
          };
        })
        .reverse();
    },
    selectedFactorComparison() {
      const rowData = this.selectedFactor();

      if (!rowData) return;

      return rowData[this.compareTo];
    },
    modalTitle() {
      return this.selectedFactor()?.name;
    },
    openDrilldownModal(selectedFactor, targetTab) {
      // No data to show in modal and no comments to display
      if (
        this.responseCount < minResponseThreshold &&
        !selectedFactor.comment_count
      )
        return;

      this.drilldownModalOpen = true;
      // Defaults to overview tab when opening modal
      this.targetedFactor = selectedFactor.id;

      setQueryStringParam("factor_id", selectedFactor.id);

      // Check if targetTab is available in visibleDrilldownTabs, fallback to overview
      if (targetTab) {
        const isTabAvailable = this.visibleDrilldownTabs.some(
          (tab) => tab.tab === targetTab,
        );
        if (!isTabAvailable) {
          targetTab = "overview";
        }
      }

      // Use targetTab if provided, otherwise check URL, otherwise use existing logic
      if (targetTab) {
        this.selectedDrilldownTab = targetTab;
        setQueryStringParam("ddt", targetTab);
      } else if (this.selectedDrilldownTab) {
        // Keep existing tab from URL (for page reloads)
        setQueryStringParam("ddt", this.selectedDrilldownTab);
      } else if (
        this.responseCount < minResponseThreshold &&
        selectedFactor.comment_count > 0
      ) {
        this.selectedDrilldownTab = "comments";
        setQueryStringParam("ddt", "comments");
      } else {
        this.selectedDrilldownTab = "overview";
        setQueryStringParam("ddt", "overview");
      }

      const selectedItem = drilldownTabs.find(
        (t) => t.tab == this.selectedDrilldownTab,
      );
      if (selectedItem?.url) {
        this.fetchTab(this.selectedDrilldownTab, selectedItem.url, "driver");
      }

      // Related workflows tends to be a slower query
      const relatedWorkflowTab = this.visibleDrilldownTabs.find(
        (t) => t.tab == "related_workflows",
      );

      if (relatedWorkflowTab) {
        this.fetchTabDataBackground(
          relatedWorkflowTab.tab,
          relatedWorkflowTab.url,
        );
      }
    },
    openDxiModal() {
      if (this.responseCount < minResponseThreshold) return;
      this.dxiModalOpen = true;
      setQueryStringParam("ddt", "overview");
      setQueryStringParam("dxi", "true");
      this.fetchTab(
        this.dxiDrilldownTabs[0].tab,
        this.dxiDrilldownTabs[0].url,
        "dxi",
      );
    },
    hideSentimentDetails() {
      this.drilldownModalOpen = false;
      this.dxiModalOpen = false;
      this.targetedFactor = null;
      this.selectedDrilldownTab = null;
      // Clear the data so we don't run into weird cache issues.
      this.drilldownData = {};
      this.trends = null;
      this.commentSearchTerm = "";
      this.commentSearchResults = [];
      this.searchCommentsLoaded = false;
      this.drilldownCommentSearchTerm = "";
      this.drilldownCommentSearchResults = [];
      this.drilldownSearchCommentsLoaded = false;

      deleteQueryStringParam("ddt");
      deleteQueryStringParam("dxi");
      deleteQueryStringParam("ddsb");
      deleteQueryStringParam("ddsr");
      deleteQueryStringParam("priv");
      deleteQueryStringParam("factor_id");
    },
    determineSquadNamesFromSquadIds() {
      const selectedSquads = this.cachedSquads.filter(
        (squad) =>
          this.squadIds.includes(String(squad.id)) ||
          this.squadIds.includes(squad.id),
      );

      this.squadNamesFromSquadIds = selectedSquads.map((squad) => squad.name);

      // Loading directly to modal from URL cached squads are empty, so wait for event
      if (this.squadIds.length != 1) {
        this.showTeamBreakdown = true;
      } else {
        this.showTeamBreakdown = this.cachedSquads.find(
          (squad) => squad.id == this.squadIds[0],
        )?.is_parent;
      }
    },
    get breakdownItems() {
      return this.getBreakdownItems({
        sortBy: this.drilldownSortBy,
        compareTo: this.compareTo,
        reverse: this.drilldownSortReverse,
        orgScore: this.selectedFactor()?.org_score,
      });
    },
    get driverItems() {
      const items = this.resultItems
        .filter((i) => i.current_snapshot)
        .sort(sortFunc(this.sortBy, this.compareTo));

      if (this.sortReverse) {
        return items.reverse();
      }

      return items;
    },
    get workflowItems() {
      if (this.drilldownData.related_workflows) {
        return this.drilldownData.related_workflows.data
          .workflow_score_and_distribution;
      }

      return [];
    },
    get datacloudChartItems() {
      if (this.drilldownData.related_workflows) {
        return this.drilldownData.related_workflows.data.datacloud_charts;
      }

      return [];
    },
    workflowComparisonValue(workflow) {
      if (this.compareTo == "vs_50th")
        return workflow.benchmark_50 == null
          ? null
          : Math.round(workflow.score - workflow.benchmark_50);
      if (this.compareTo == "vs_75th")
        return workflow.benchmark_75 == null
          ? null
          : Math.round(workflow.score - workflow.benchmark_75);
      if (this.compareTo == "vs_90th")
        return workflow.benchmark_90 == null
          ? null
          : Math.round(workflow.score - workflow.benchmark_90);
      if (this.compareTo == "vs_prev")
        return workflow.prev_percentage == null
          ? null
          : Math.round(workflow.score - workflow.prev_percentage);
      if (this.compareTo == "vs_org")
        return Math.round(workflow.score - workflow.org_score);
    },
    workflowAvgValueComparison(workflow) {
      if (this.compareTo == "vs_50th") {
        return workflow.avg_value_percent_diff_50 ?? null;
      } else if (this.compareTo == "vs_75th") {
        return workflow.avg_value_percent_diff_75 ?? null;
      } else if (this.compareTo == "vs_90th") {
        return workflow.avg_value_percent_diff_90 ?? null;
      } else if (this.compareTo == "vs_prev") {
        return workflow.avg_value_percent_diff_prev ?? null;
      } else if (this.compareTo == "vs_org") {
        return workflow.avg_value_percent_diff_org ?? null;
      }
      return null;
    },
    get maxDriverScore() {
      const arr = this.driverItems.map((i) => i.score);

      return Math.max(...arr);
    },
    get maxPriority() {
      const arr = this.driverItems.map((i) => i.priority);

      return Math.max(...arr);
    },
    targetedPositions(workflow) {
      if (workflow.target_direction == "u")
        return arrayRange(0, workflow.target_position, 1);

      return arrayRange(
        workflow.target_position,
        maxBehavioralQuestions - 1,
        1,
      ).reverse();
    },
    percentageWidthForPosition(workflow, position) {
      return `width: ${this.workflowPercentageOfResponses(
        workflow,
        position,
        "count",
      )}`;
    },
    workflowPercentageOfResponses(workflow, position) {
      if (workflow["count"] === 0) return "0%";

      return `${Math.round(
        (100 * workflow[targetPositionCountMap.get(position)]) /
          workflow["count"],
      )}%`;
    },
    upDirection(workflow) {
      return workflow.target_direction == "u";
    },
    nonTargetedPositions(workflow) {
      if (workflow.target_direction == "u")
        return arrayRange(
          workflow.target_position + 1,
          maxBehavioralQuestions - 1,
          1,
        );

      return arrayRange(0, workflow.target_position - 1, 1).reverse();
    },
    isSortOrder(order) {
      return this.sortBy == order;
    },
    isDrilldownSortOrder(order) {
      return this.drilldownSortBy == order;
    },
    changeComparison(compareTo) {
      this.$store.snapshotResultFilters.setCompareTo(compareTo);
      if (this.compareTo == "vs_prev") this.fetchAll();
      // Update any trends comparison data if it's showing
      if (this.selectedDrilldownTab == "overview" && this.drilldownModalOpen) {
        this.renderDriverTrendChart();
      }
      if (this.selectedDrilldownTab == "overview" && this.dxiModalOpen) {
        this.renderDxiTrendChart();
      }
    },
    changeSortBy(sortBy) {
      if (this.sortBy == sortBy) {
        this.$store.snapshotResultFilters.sortReverse = !this.sortReverse;

        setQueryStringParam("sr", this.sortReverse);
        return;
      }

      this.$store.snapshotResultFilters.sortBy = sortBy;
      this.$store.snapshotResultFilters.sortReverse = false;

      setQueryStringParam("sb", sortBy);
      setQueryStringParam("sr", false);
    },
    changeDrilldownSortBy(sortBy) {
      if (this.drilldownSortBy == sortBy) {
        this.$store.snapshotResultFilters.drilldownSortReverse =
          !this.drilldownSortReverse;

        setQueryStringParam("ddsr", this.drilldownSortReverse);
        return;
      }

      this.$store.snapshotResultFilters.drilldownSortBy = sortBy;
      this.$store.snapshotResultFilters.drilldownSortReverse = false;

      setQueryStringParam("ddsb", sortBy);
      setQueryStringParam("ddsr", false);
    },
    // Note: changeSquadIds and changeGroupIds methods are provided by peopleFiltersMixin

    changeTagIds(tagIds) {
      this.$store.snapshotResultFilters.tagIds = tagIds;
      setQueryStringParam("tag_ids", tagIds);

      this.fetchAll();

      // Only clear tree state and refresh team breakdown if modal is actually open
      if (
        this.selectedDrilldownTab === "team_breakdown" &&
        this.drilldownModalOpen
      ) {
        this.resetTreeState();
        this.refetchTeamBreakdown();
      }
    },
    fetchAll() {
      // fetchComments sets this to false as well, but is not called until other data has been loaded
      this.commentsLoaded = false;
      this.allComments = [];
      this.commentIds = new Set();

      return this.fetchResultItems()
        .then(() => {
          // If filters have changed then reset the comments
          this.fetchComments(true);
          this.determineSquadNamesFromSquadIds();
        })
        .catch((error) => {
          console.error("Error fetching items", error);
        });
    },
    handleScroll() {
      if (!this.$refs.allCommentsContainer) return;

      const { scrollTop, offsetHeight, scrollHeight } =
        this.$refs.allCommentsContainer;

      const fetchMore = scrollTop + offsetHeight + 20 > scrollHeight;

      if (fetchMore && this.hasMoreComments) {
        this.fetchComments();
      }
    },
    fetchComments(resetComments = false) {
      this.commentsLoaded = false;
      this.commentPage = resetComments ? 1 : this.commentPage + 1;

      const paramsObj = {
        page: this.commentPage,
        limit: COMMENT_PAGE_SIZE,
        squad_ids: this.squadIds.join(","),
        group_ids: this.groupIds.join(","),
        tag_ids: this.tagIds.join(","),
        branch: this.$store.snapshotResultFilters.branch,
      };

      const queryParams = objectToQueryString(paramsObj);

      let url = `${baseUrl}/comments?${queryParams}`;

      return fetch(url)
        .then((resp) => resp.json())
        .then((data) => {
          if (resetComments) {
            this.allComments = data.slice(0, COMMENT_PAGE_SIZE);
            this.commentIds = new Set(
              this.allComments.map((comment) => comment.id),
            );
          } else {
            const commentsToAppend = data
              .slice(0, COMMENT_PAGE_SIZE)
              .filter((comment) => {
                return !this.commentIds.has(comment.id);
              });
            this.allComments = this.allComments.concat(commentsToAppend);
            commentsToAppend.forEach((comment) =>
              this.commentIds.add(comment.id),
            );
          }

          this.hasMoreComments = data.length > COMMENT_PAGE_SIZE;

          this.commentsLoaded = true;
        });
    },
    generateDxiTrendComparisonValues() {
      // If you update logic here, be sure to update get_dxi_benchmarks in app/services/reports/core4/dxi.rb
      // which is used in all other areaas of the app (reports, dashboard, benchmark report builder, etc)

      let benchmarkScore;

      if (
        this.compareTo == "vs_50th" ||
        this.compareTo == "vs_75th" ||
        this.compareTo == "vs_90th"
      ) {
        // Since we show the user the relative value (-1 vs P75, -8 vs P90), use the relative value for the trend
        // chart to avoid confusion.
        benchmarkScore =
          Math.round(this.dxiComparisonValue) * -1 + this.dxiScore;
      } else if (this.compareTo == "vs_org") {
        benchmarkScore = this.orgDxiScore;
      }

      return this.trends.values.map((item) => {
        return {
          date: item.date,
          label: comparisonOptionsMap.get(this.compareTo),
          value: benchmarkScore,
        };
      });
    },
    renderDxiTrendChart() {
      const elChart = document.getElementById(`dxi-trends-chart`);
      if (!elChart) return;

      elChart.innerHTML = null;
      const benchmark = "vs_prev";
      const minVal = Math.min(...this.trends.values.map((v) => v.value));
      const maxVal = Math.max(...this.trends.values.map((v) => v.value));

      const trendSize = this.trends.values.length;
      this.xEnd = this.trends.values[trendSize - 1]?.label;
      // Avoid using 'this' in passing in function for the tooltip callback, get the string explicitly
      const heading = "DXI";

      const attrs = {
        elChart: elChart,
        startDate: this.xStart,
        endDate: this.trends.values[trendSize - 1]?.date,
        tooltipHtml(d, cd) {
          const params = {
            heading: heading,
            d: {
              label: scrubHtml(d.label),
              value: d.value,
              formattedValue: d.value,
            },
          };

          if (cd) {
            params.cd = {
              label: cd.label,
              value: cd.value,
              formattedValue: cd.value,
            };
          }

          return chartTooltipHtml.default(params);
        },
        yAxisMin: minVal * 0.8,
        yAxisMax: maxVal * 1.2,
        showXAxisTicks: false,
        values: this.trends.values,
      };

      if (
        ["vs_50th", "vs_75th", "vs_90th", "vs_org"].includes(this.compareTo)
      ) {
        attrs.comparisonValues = this.generateDxiTrendComparisonValues();
      }

      if (this.compareTo == "vs_prev") {
        attrs.comparisonValues = generateComparisonValues(
          this.trends,
          benchmark,
        );
      }

      return new LineChart(attrs);
    },
    fetchTabDataBackground(tab, url) {
      if (!this.selectedFactor) return;

      return fetch(`${url}${this.queryParams()}`)
        .then((resp) => resp.json())
        .then((data) => {
          this.drilldownData[tab] = {
            data,
            benchmark_factor: data.benchmark_factor || null,
          };
        });
    },
    fetchTab(tab, url, modalView) {
      this.tabDataLoaded = false;

      if (tab == "comments") {
        // Reset visibility setting when loading new comments tab
        this.commentVisibilitySetting = "all";
        // Reset the drilldown search term when opening comments tab
        this.drilldownCommentSearchTerm = "";
        this.drilldownCommentSearchResults = [];
        this.drilldownSearchCommentsLoaded = false;
        // Remove the priv parameter from the URL
        deleteQueryStringParam("priv");
      }

      if (tab === "team_breakdown") {
        this.resetTreeState();
        this.isFetchingTableData = true;

        // Set default drilldown sort if not specified in URL
        if (!getQueryStringParams().get("ddsb")) {
          this.$store.snapshotResultFilters.drilldownSortBy =
            "snapshotSquadName";
          this.$store.snapshotResultFilters.drilldownSortReverse = false;
          setQueryStringParam("ddsb", "snapshotSquadName");
          setQueryStringParam("ddsr", "false");
        }
      }

      this.selectedDrilldownTab = tab;
      setQueryStringParam("ddt", tab);

      const fullUrl = `${url}${this.queryParams()}`;

      if (!this.selectedFactor) return;

      return fetch(fullUrl, { headers: { Accept: "application/json" } })
        .then(async (resp) => {
          if (!resp.ok) {
            const body = await resp.text();
            throw new Error(
              `HTTP ${resp.status}: ${resp.statusText} for ${fullUrl} - ${body.substring(0, 200)}`,
            );
          }
          const contentType = resp.headers.get("content-type") || "";
          if (!contentType.includes("application/json")) {
            const body = await resp.text();
            throw new Error(
              `Non-JSON response (${contentType}) for ${fullUrl}: ${body.substring(0, 200)}`,
            );
          }
          return resp.json();
        })
        .then((data) => {
          if (tab == "overview" && modalView == "driver") {
            // Fallback to null as undefined will not render properly
            data.trends[0].factor["vs_50th"] =
              data.benchmark_factor?.p_50 || null;
            data.trends[0].factor["vs_75th"] =
              data.benchmark_factor?.p_75 || null;
            data.trends[0].factor["vs_90th"] =
              data.benchmark_factor?.p_90 || null;
          }

          // Benchmark data is now provided directly by the server for each row
          // No need to apply client-side

          if (
            tab == "related_workflows" &&
            data.workflow_score_and_distribution
          ) {
            data.workflow_score_and_distribution.forEach((workflow) => {
              const benchmarkBehavioralQuestion =
                data.benchmark_behavioral_questions[workflow["id"]];

              if (benchmarkBehavioralQuestion) {
                workflow["benchmark_50"] = benchmarkBehavioralQuestion["p_50"];
                workflow["benchmark_75"] = benchmarkBehavioralQuestion["p_75"];
                workflow["benchmark_90"] = benchmarkBehavioralQuestion["p_90"];
                // Calculate differences for benchmark comparisons
                if (
                  workflow.avg_value !== null &&
                  workflow.avg_value !== undefined
                ) {
                  const calculateDifference = (benchmarkValue) =>
                    benchmarkValue ? workflow.avg_value - benchmarkValue : null;

                  const calculatePercentageDiff = (benchmarkValue) =>
                    benchmarkValue && benchmarkValue !== 0
                      ? ((workflow.avg_value - benchmarkValue) /
                          Math.abs(benchmarkValue)) *
                        100
                      : null;

                  // Value differences
                  workflow["avg_value_diff_50"] = calculateDifference(
                    benchmarkBehavioralQuestion["p_50"],
                  );
                  workflow["avg_value_diff_75"] = calculateDifference(
                    benchmarkBehavioralQuestion["p_75"],
                  );
                  workflow["avg_value_diff_90"] = calculateDifference(
                    benchmarkBehavioralQuestion["p_90"],
                  );

                  // Percentage differences
                  workflow["avg_value_percent_diff_50"] =
                    calculatePercentageDiff(
                      benchmarkBehavioralQuestion["p_50"],
                    );
                  workflow["avg_value_percent_diff_75"] =
                    calculatePercentageDiff(
                      benchmarkBehavioralQuestion["p_75"],
                    );
                  workflow["avg_value_percent_diff_90"] =
                    calculatePercentageDiff(
                      benchmarkBehavioralQuestion["p_90"],
                    );

                  // Previous period calculations
                  if (workflow.previous_avg_value != null) {
                    workflow["avg_value_percent_diff_prev"] =
                      calculatePercentageDiff(workflow.previous_avg_value);
                  }

                  // Org calculations
                  if (workflow.org_avg_value != null) {
                    workflow["avg_value_percent_diff_org"] =
                      calculatePercentageDiff(workflow.org_avg_value);
                  }
                }
              }
            });

            data.workflow_trends.forEach((trendRow) => {
              const benchmarkBehavioralQuestion =
                data.benchmark_behavioral_questions[trendRow.workflow["id"]];

              if (benchmarkBehavioralQuestion) {
                trendRow.workflow["benchmark_50"] =
                  benchmarkBehavioralQuestion["p_50"];
                trendRow.workflow["benchmark_75"] =
                  benchmarkBehavioralQuestion["p_75"];
                trendRow.workflow["benchmark_90"] =
                  benchmarkBehavioralQuestion["p_90"];
              }
            });
          }

          this.drilldownData[tab] = {
            data,
            benchmark_factor: data.benchmark_factor || null,
          };
          if (tab == "comments") {
            this.hasTeamOnlyComments = data.some(
              (comment) => comment.text_visible_to == "team_only",
            );

            this.hasGlobalComments = data.some(
              (comment) => comment.text_visible_to == "all",
            );

            this.hasAdminOnlyComments =
              currentUser.is_admin &&
              (this.selectedFactor()?.comment_count_admin_only || 0) > 0;

            // Auto-switch to appropriate visibility setting if current setting has no comments
            const currentVisibilityHasComments = data.some(
              (comment) =>
                comment.text_visible_to == this.commentVisibilitySetting,
            );

            if (!currentVisibilityHasComments && data.length > 0) {
              // Switch to the first available comment type
              if (this.hasGlobalComments) {
                this.commentVisibilitySetting = "all";
              } else if (this.hasTeamOnlyComments) {
                this.commentVisibilitySetting = "team_only";
              } else if (this.hasAdminOnlyComments) {
                this.commentVisibilitySetting = "admin_only";
              }
            }
          }
          if (tab === "team_breakdown") {
            this.tableDataLoaded = true;
            this.isFetchingTableData = false;
          }

          this.tabDataLoaded = true;
          this.renderTabDetails(tab, data, modalView);
        })
        .catch((error) => console.log("Error fetching tab", tab, error));
    },
    get filteredComments() {
      return (
        this.drilldownData?.comments?.data?.filter(
          (comment) => comment.text_visible_to == this.commentVisibilitySetting,
        ) || []
      );
    },
    get noCommentsMessage() {
      if (this.commentVisibilitySetting == "all") {
        return "There are no global comments";
      } else if (this.commentVisibilitySetting == "team_only") {
        return "There are no limited comments";
      } else if (this.commentVisibilitySetting == "admin_only") {
        return "There are no admin only comments";
      }
      return "There are no comments";
    },
    xStartLabel() {
      if (!this.xStart) return "";
      return new Intl.DateTimeFormat("en-US", {
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      }).format(new Date(this.xStart));
    },
    renderDriverTrendChart() {
      const elChart = document.getElementById("driver-trends-chart");
      if (!elChart) return;

      elChart.innerHTML = null;
      const benchmark = this.compareTo;
      const minVal = Math.min(
        ...this.trends.values
          .map((v) => v.value)
          .concat(this.trends.factor.vs_50th),
      );
      const maxVal = Math.max(
        ...this.trends.values
          .map((v) => v.value)
          .concat(this.trends.factor.vs_90th),
      );

      const trendSize = this.trends.values.length;
      this.xEnd = this.trends.values[trendSize - 1]?.label;
      // Avoid using 'this' in passing in function for the tooltip callback, get the string explicitly
      const heading = this.trends.factor.name;

      const attrs = {
        elChart: elChart,
        startDate: this.xStart,
        endDate: this.trends.values[trendSize - 1]?.date,
        tooltipHtml(d, cd) {
          const params = {
            heading: heading,
            d: {
              label: d.label,
              value: d.value,
              formattedValue: d.value,
            },
          };

          if (cd) {
            params.cd = {
              label: cd.label,
              value: cd.value,
              formattedValue: cd.value,
            };
          }

          return chartTooltipHtml.default(params);
        },
        yAxisMin: minVal * 0.8,
        yAxisMax: maxVal * 1.2,
        showXAxisTicks: false,
        values: this.trends.values,
      };

      if (
        benchmark &&
        (this.trends.factor[benchmark] || benchmark === "vs_prev")
      ) {
        attrs.comparisonValues = generateComparisonValues(
          this.trends,
          benchmark,
        );
      }

      if (benchmark == "vs_org") {
        attrs.comparisonValues = generateOrgComparisonValues(
          this.trends,
          this.selectedFactor()?.score - this.selectedFactorComparison(),
        );
      }

      return new LineChart(attrs);
    },
    renderDriverDistributionChart() {
      if (!this.hasMultipleTrendValues) return;
      const elChart = document.getElementById(`driver-distribution-chart`);
      if (!elChart) return;

      elChart.innerHTML = null;
      const benchmark = this.compareTo;
      const minVal = Math.min(
        ...this.trends.values
          .map((v) => v.value)
          .concat(this.trends.factor.vs_50th),
      );
      const maxVal = Math.max(
        ...this.trends.values
          .map((v) => v.value)
          .concat(this.trends.factor.vs_90th),
      );

      const trendSize = this.trends.values.length;
      this.xEnd = this.trends.values[trendSize - 1]?.label;
      // Avoid using 'this' in passing in function for the tooltip callback, get the string explicitly

      const colors = getDistributionColors(this.selectedFactorIs5Point ? 5 : 3);
      let series;

      if (this.selectedFactorIs5Point) {
        series = [
          {
            key: "one_count",
            name: "Never",
            color: colors[4],
            counts: [],
          },
          {
            key: "two_count",
            name: "Rarely",
            color: colors[3],
            counts: [],
          },
          {
            key: "three_count",
            name: "Sometimes",
            color: colors[2],
            counts: [],
          },
          {
            key: "four_count",
            name: "Very often",
            color: colors[1],
            counts: [],
          },
          {
            key: "five_count",
            name: "Always",
            color: colors[0],
            counts: [],
          },
        ];
      } else {
        series = [
          {
            key: "one_count",
            name: "Bad",
            color: colors[2],
            counts: [],
          },
          {
            key: "two_count",
            name: "So-so",
            color: colors[1],
            counts: [],
          },
          {
            key: "three_count",
            name: "Good",
            color: colors[0],
            counts: [],
          },
        ];
      }

      let stackedAreaValues = {
        series: series,
        dates: [],
      };

      // Formatting the trend data to match the required format the stacked area chart needs
      this.trends.values.forEach((val) => {
        stackedAreaValues.dates.push(val.date);

        stackedAreaValues.series.forEach((breakdownValue) => {
          breakdownValue.counts.push(val.breakdown[breakdownValue.key]);
        });
      });

      const attrs = {
        elChart: elChart,
        startDate: this.xStart,
        endDate: this.trends.values[trendSize - 1]?.date,
        tooltipHtml(d, cd) {
          const params = {
            heading: new Intl.DateTimeFormat("en-US", {
              month: "short",
              year: "numeric",
              timeZone: "UTC",
            }).format(d),
            rows: cd
              .map((r) => ({
                label: r.name,
                value: r.count,
                formattedValue: Math.round(r.percentage * 100) + "%",
                color: r.color,
              }))
              .reverse(),
          };

          return chartTooltipHtml.default(params);
        },
        yAxisMin: minVal * 0.8,
        yAxisMax: maxVal * 1.2,
        showXAxisTicks: false,
        values: stackedAreaValues,
      };

      if (
        benchmark &&
        (this.trends.factor[benchmark] || benchmark === "vs_prev")
      ) {
        attrs.comparisonValues = generateComparisonValues(
          this.trends,
          benchmark,
        );
      }

      return new StackedAreaChart(attrs);
    },
    renderTabDetails(tab, data, modalView) {
      if (modalView == "driver") {
        if (tab == "overview" && this.responseCount >= minResponseThreshold) {
          this.trends = data.trends[0];
          // Find first value with non-null data (matching what the chart displays)
          const firstValidValue = this.trends.values.find(
            (v) => v.value !== null && v.value !== undefined && !isNaN(v.value),
          );
          this.xStart = firstValidValue
            ? firstValidValue.date
            : this.trends.values[0].date;
          this.renderDriverTrendChart();
          this.renderDriverDistributionChart();
        }
        if (tab == "team_breakdown") {
          this.calculateMaxTeamPriority();
          this.calculateMaxTeamScoreForDriver();
        }
        if (tab == "priority") {
          this.calculateRankChange();
          this.renderBumpChart();
        }
        if (tab == "related_workflows") {
          // Find earliest valid value across all workflow trends
          let earliestValidDate = null;
          if (data.workflow_trends && data.workflow_trends.length > 0) {
            data.workflow_trends.forEach((trend) => {
              const firstValid = trend.values.find(
                (v) =>
                  v.value !== null && v.value !== undefined && !isNaN(v.value),
              );

              if (
                firstValid &&
                (!earliestValidDate || firstValid.date < earliestValidDate)
              ) {
                earliestValidDate = firstValid.date;
              }
            });
          }

          this.xStart = earliestValidDate || data.x_start;
          this.renderRelatedWorkflowsCharts();
        }
      }

      if (modalView == "dxi") {
        if (tab == "overview") {
          this.trends = data.trends;
          this.xStart = this.trends.values[0].date;
          setTimeout(() => {
            this.renderDxiTrendChart();
          }, 100);
        }
        if (tab == "team_breakdown") {
          // While DXI is not a driver, it has the same field
          this.calculateMaxTeamScoreForDriver();
        }
      }
    },
    renderRelatedWorkflowsCharts() {
      if (!this.drilldownData.related_workflows?.data?.workflow_trends) return;

      const workflowData = this.drilldownData.related_workflows.data;

      if (!Array.isArray(workflowData.workflow_score_and_distribution)) return;

      workflowData.workflow_score_and_distribution.forEach((workflow) => {
        const trendData = workflowData.workflow_trends.find((trend) => {
          return trend.workflow.id == workflow.id;
        });

        this.renderWorkflowTrendChart(workflow, trendData);
        this.renderWorkflowDistributionChart(workflow, trendData);
      });
    },
    workflowTrendEnd(workflow) {
      if (!this.drilldownData.related_workflows?.data?.workflow_trends) return;

      const trendData =
        this.drilldownData.related_workflows.data.workflow_trends.find(
          (trend) => {
            return trend.workflow.id == workflow.id;
          },
        );

      if (!trendData) return;

      return trendData.values[trendData.values.length - 1].label;
    },
    workflowTrendStart(workflow) {
      if (!this.drilldownData.related_workflows?.data?.workflow_trends) return;

      const trendData =
        this.drilldownData.related_workflows.data.workflow_trends.find(
          (trend) => {
            return trend.workflow.id == workflow.id;
          },
        );

      if (!trendData) return;

      // Find first valid value (skip nulls)
      const firstValid = trendData.values.find(
        (v) => v.value !== null && v.value !== undefined && !isNaN(v.value),
      );

      return firstValid?.label || trendData.values[0].label;
    },
    hasMultipleTrendValuesForWorkflow(workflow) {
      if (!this.drilldownData.related_workflows?.data?.workflow_trends) return;

      const trendData =
        this.drilldownData.related_workflows.data.workflow_trends.find(
          (trend) => {
            return trend.workflow.id == workflow.id;
          },
        );

      if (!trendData) return;

      const validValues = trendData.values?.filter(
        (v) => v.value !== null && v.value !== undefined && !isNaN(v.value),
      );
      return validValues?.length >= 3;
    },
    workflowDistributionItems(workflow) {
      if (!workflow) return [];

      // Parse behavioral options from JSON
      let options;
      try {
        options = workflow.options ? JSON.parse(workflow.options) : null;
      } catch (e) {
        console.error("Failed to parse workflow options:", e);
        return [];
      }
      if (!options) return [];

      // Get targeted positions from workflow
      const targetedSet = new Set(workflow.targeted_positions || []);

      // Get total responses for percentage calculation
      const totalResponses = workflow.sample_size || 0;

      // Create items for all positions (0-6)
      return [0, 1, 2, 3, 4, 5, 6]
        .filter((position) => options[position]) // Only include if option exists
        .map((position) => {
          const count = workflow.breakdown_counts?.[position] || 0;
          const percentage =
            totalResponses > 0
              ? `${Math.round((count / totalResponses) * 100)}%`
              : "0%";

          return {
            label: options[position] || "",
            count: count,
            percentage: percentage,
            targeted: targetedSet.has(position),
          };
        });
    },
    renderWorkflowTrendChart(workflow, trendData) {
      if (!trendData) return;

      const chartEl = document.getElementById(`workflow-${workflow.id}-chart`);
      if (!chartEl) return;

      chartEl.innerHTML = null;

      const benchmark = this.compareTo;
      // Filter out null values before calculating min/max
      const validValues = trendData.values
        .map((v) => v.value)
        .filter((val) => val !== null && val !== undefined && !isNaN(val));

      const benchmarkValues = [
        workflow.benchmark_50,
        workflow.benchmark_90,
      ].filter((val) => val !== null && val !== undefined && !isNaN(val));

      const allValues = validValues.concat(benchmarkValues);

      if (allValues.length === 0) {
        console.warn("No valid values found for trend chart", workflow.name);
        return;
      }

      const minVal = Math.min(...allValues);
      const maxVal = Math.max(...allValues);

      const filteredValues = trendData.values.filter(
        (v) => v.value !== null && v.value !== undefined && !isNaN(v.value),
      );

      // Use this workflow's first valid date, not the shared xStart
      const xStart = filteredValues[0]?.date || this.xStart;
      const xEnd =
        filteredValues[filteredValues.length - 1]?.date ||
        trendData.values[trendData.values.length - 1].date;

      const attrs = {
        elChart: chartEl,
        startDate: xStart,
        endDate: xEnd,
        tooltipHtml(d, cd) {
          const params = {
            heading: workflow.name,
            d: {
              label: d.label,
              value: d.value,
              formattedValue: parseFloat(d.value).toFixed(2),
            },
          };

          if (cd) {
            params.cd = {
              label: cd.label,
              value: cd.value,
              formattedValue: parseFloat(cd.value).toFixed(2),
            };
          }

          return chartTooltipHtml.workflowAverage(params);
        },
        yAxisMin: minVal * 0.8,
        yAxisMax: maxVal * 1.2,
        showXAxisTicks: false,
        values: filteredValues,
      };

      if (
        benchmark &&
        (workflow[benchmarkDiffConversions.get(benchmark)] ||
          benchmark === "vs_prev")
      ) {
        attrs.comparisonValues = generateComparisonValues(
          trendData,
          benchmarkDiffConversions.get(benchmark),
          "workflow",
        );
      }
      if (benchmark == "vs_org") {
        attrs.comparisonValues = generateOrgComparisonValues(
          trendData,
          workflow.org_avg_value,
        );
      }

      return new LineChart(attrs);
    },
    renderWorkflowDistributionChart(workflow, trendData) {
      let chartElement = document.getElementById(
        `workflow-${workflow.id}-distribution-chart`,
      );

      if (!workflow || !trendData || !chartElement) return;

      chartElement.innerHTML = null;

      const benchmark = this.compareTo;
      const minVal = Math.min(
        ...trendData.values.map((v) => v.value).concat(workflow.benchmark_50),
      );
      const maxVal = Math.max(
        ...trendData.values.map((v) => v.value).concat(workflow.benchmark_90),
      );
      const xEnd = trendData.values[trendData.values.length - 1].date;

      let options = JSON.parse(trendData.values[0]?.options);
      let needsReversing = workflow?.target_direction === "d";
      if (needsReversing) options = options.reverse();

      const colors = getDistributionColors(options.length);

      let stackedAreaValues = { series: [], dates: [] };

      options.forEach((opt, idx) => {
        let indexToUse = idx;
        // Go backwards through the list of options to ensure ordering matches the distribution chart above
        if (needsReversing) indexToUse = options.length - (idx + 1);

        stackedAreaValues.series.push({
          name: opt,
          color: colors[idx], // This will use the appropriate color based on the number of options
          counts: [],
          key: `${numberToSpelling(indexToUse)}_position_count`,
        });
      });

      // Formatting the trend data to match the required format the stacked area chart needs
      trendData.values.forEach((val) => {
        stackedAreaValues.dates.push(val.date);

        stackedAreaValues.series.forEach((breakdownValue) => {
          breakdownValue.counts.push(val.breakdown[breakdownValue.key]);
        });
      });

      // NOTE: reversing here so that the stacks matches the order of the distribution chart above
      stackedAreaValues.series = stackedAreaValues.series.reverse();

      const attrs = {
        elChart: chartElement,
        startDate: this.xStart,
        endDate: xEnd,
        tooltipHtml(d, cd) {
          const params = {
            heading: new Intl.DateTimeFormat("en-US", {
              month: "short",
              year: "numeric",
              timeZone: "UTC",
            }).format(d),
            rows: cd
              .map((r) => ({
                label: r.name,
                value: r.count,
                formattedValue: Math.round(r.percentage * 100) + "%",
                color: r.color,
              }))
              .reverse(),
          };

          return chartTooltipHtml.default(params);
        },
        yAxisMin: minVal * 0.8,
        yAxisMax: maxVal * 1.2,
        showXAxisTicks: false,
        values: stackedAreaValues,
      };

      if (
        benchmark &&
        (workflow[benchmarkDiffConversions.get(benchmark)] ||
          benchmark === "vs_prev")
      ) {
        attrs.comparisonValues = generateComparisonValues(
          trendData,
          benchmarkDiffConversions.get(benchmark),
          "workflow",
        );
      }

      return new StackedAreaChart(attrs);
    },

    calculateMaxTeamPriority() {
      const arr = this.breakdownItems.map((i) => i.priority);

      this.maxTeamPriority = Math.max(...arr);
    },
    calculateMaxTeamScoreForDriver() {
      const arr = this.breakdownItems.map((i) => i.score);

      this.maxTeamScoreForDriver = Math.max(...arr);
    },
    currentRank() {
      if (this.drilldownData.priority?.data) {
        const ranks = this.drilldownData.priority.data.series.filter(
          (i) => i.id == this.targetedFactor,
        )[0].ranks;

        return ranks[ranks.length - 1];
      }
    },
    previousRank() {
      if (this.drilldownData.priority?.data) {
        const ranks = this.drilldownData.priority.data.series.filter(
          (i) => i.id == this.targetedFactor,
        )[0].ranks;

        return ranks[ranks.length - 2];
      }
    },
    get targetedFactorPriorityVotes() {
      return this.selectedFactor()?.priority;
    },
    calculateRankChange() {
      if (!this.previousRank()) return;

      const amount = this.currentRank() - this.previousRank();
      this.selectedFactorRankChange = amount;
    },
    renderBumpChart() {
      const elChart = document.getElementById(`bumpchart`);
      if (!elChart) return;

      elChart.innerHTML = null;

      new BumpChart({
        elChart: document.querySelector("#bumpchart"),
        data: this.drilldownData.priority.data,
        focusedOn: this.viewAllDrivers ? null : this.selectedFactor()?.name,
      });
    },
    queryParams() {
      let paramObj = {
        ct: this.compareTo,
        prev_ss: this.previousSnapshotId,
        squad_ids: `${this.squadIds.join(",")}`,
        group_ids: `${this.groupIds.join(",")}`,
        tag_ids: `${this.tagIds.join(",")}`,
        factor_id: this.targetedFactor,
        branch: this.$store.snapshotResultFilters.branch,
        aggregation:
          this.$store.snapshotResultFilters.aggregation ||
          getQueryStringParams().get("aggregation"),
      };

      return `?${objectToQueryString(paramObj)}`;
    },
    calculateItemComparison(item) {
      if (this.compareTo == "vs_50th" && item.benchmark_50) {
        return item.score - item.benchmark_50;
      } else if (this.compareTo == "vs_75th" && item.benchmark_75) {
        return item.score - item.benchmark_75;
      } else if (this.compareTo == "vs_90th" && item.benchmark_90) {
        return item.score - item.benchmark_90;
      } else if (this.compareTo == "vs_prev") {
        return item.previous_score != null
          ? item.score - item.previous_score
          : null;
      } else if (this.compareTo == "vs_org") {
        return item.score - this.selectedFactor()?.org_score;
      }

      return null;
    },
    get atlasStrategies() {
      if (this.drilldownData.recommendations?.data) {
        return this.drilldownData.recommendations.data.strategies;
      }

      return [];
    },
    get atlasArticle() {
      if (this.drilldownData.recommendations?.data) {
        return this.drilldownData.recommendations.data.article;
      }

      return null;
    },
    get filtersApplied() {
      return (
        this.$store.snapshotResultFilters.tagIds.length ||
        this.$store.snapshotResultFilters.squadIds.length ||
        this.$store.snapshotResultFilters.groupIds.length
      );
    },
    fetchResultItems() {
      this.resultItemsLoaded = false;

      let url = `${baseUrl}/items`;

      const paramsObj = {
        ct: this.compareTo,
        prev_ss: this.previousSnapshotId,
        squad_ids: this.squadIds.join(","),
        group_ids: this.groupIds.join(","),
        tag_ids: this.tagIds.join(","),
        branch: this.$store.snapshotResultFilters.branch,
        public_and_private: "true",
      };

      const queryParams = objectToQueryString(paramsObj);
      const fullUrl = `${url}?${queryParams}`;

      return fetch(fullUrl)
        .then((resp) => resp.json())
        .then((data) => {
          if (typeof data.benchmark_factors == "object") {
            data.result_items.forEach((row) => {
              const benchmarkFactor = data.benchmark_factors[row["id"]];

              if (
                benchmarkFactor &&
                row["score"] !== null &&
                row["score"] !== undefined
              ) {
                row["benchmark_50"] = benchmarkFactor["p_50"];
                row["benchmark_75"] = benchmarkFactor["p_75"];
                row["benchmark_90"] = benchmarkFactor["p_90"];

                row["vs_50th"] = row["score"] - benchmarkFactor["p_50"];
                row["vs_75th"] = row["score"] - benchmarkFactor["p_75"];
                row["vs_90th"] = row["score"] - benchmarkFactor["p_90"];
              }
            });
          }

          this.resultItems = data.result_items;
          this.responseCount = data.response_count;
          this.recipientCount = data.recipient_count;
          this.commentSummaries = data.comment_summaries;
          this.dxiScore = data.dxi_score;
          this.orgDxiScore = data.org_dxi_score;
          this.previousDxiScore = data.previous_dxi_score;
          this.benchmarkableDxiScore = data.benchmarkable_dxi_score;
          this.dxi50 = this.calculateDxiBenchmark("vs_50th");
          this.dxi75 = this.calculateDxiBenchmark("vs_75th");
          this.dxi90 = this.calculateDxiBenchmark("vs_90th");

          this.resultItemsLoaded = true;
        })
        .catch((error) => {
          console.error("Error fetching items", error);
        });
    },
    toggleViewAllDrivers() {
      this.viewAllDrivers = !this.viewAllDrivers;
      this.renderBumpChart();
    },
    hideSearch() {
      if (this.selectedDrilldownTab === "comments") {
        this.$refs.drilldownSearchField?.blur();
      } else {
        this.$refs.sidebarSearchField?.blur();
        this.showCommentsSearch = false;
      }
    },
    showSearch() {
      this.showCommentsSearch = true;
      setTimeout(() => {
        this.$refs.sidebarSearchField?.focus();
      }, 50);
    },
    shouldBlur() {
      if (this.selectedDrilldownTab === "comments") {
        if (this.drilldownCommentSearchTerm.trim() == "") {
          this.$refs.drilldownSearchField?.blur();
        }
      } else if (this.showCommentsSearch) {
        if (this.commentSearchTerm.trim() == "") {
          this.$refs.sidebarSearchField?.blur();
          this.showCommentsSearch = false;
        }
      }
    },
    clearSearchTerm() {
      if (this.selectedDrilldownTab === "comments") {
        this.drilldownCommentSearchTerm = "";
        this.drilldownCommentSearchResults = [];
        this.drilldownSearchCommentsLoaded = false;
      } else {
        this.commentSearchTerm = "";
        this.commentSearchResults = [];
        this.searchCommentsLoaded = false;
      }
    },
    performCommentSearch() {
      const isInDrilldown = this.selectedDrilldownTab === "comments";
      const searchTerm = isInDrilldown
        ? this.drilldownCommentSearchTerm
        : this.commentSearchTerm;

      // Set the appropriate loading state to false
      if (isInDrilldown) {
        this.drilldownSearchCommentsLoaded = false;
      } else {
        this.searchCommentsLoaded = false;
      }

      const finalTerm = searchTerm.trim();

      if (finalTerm.length > 2) {
        const queryParamObject = {
          term: finalTerm,
          factor_id: this.targetedFactor,
          branch: this.$store.snapshotResultFilters.branch,
          squad_ids: this.squadIds.join(","),
          tag_ids: this.tagIds.join(","),
        };

        const fullUrl = `${baseUrl}/comments/search?${objectToQueryString(queryParamObject)}`;

        return fetch(fullUrl)
          .then((resp) => resp.json())
          .then((data) => {
            // Update the appropriate search results and loading state
            if (isInDrilldown) {
              this.drilldownCommentSearchResults = data;
              this.drilldownSearchCommentsLoaded = true;
            } else {
              this.commentSearchResults = data;
              this.searchCommentsLoaded = true;
            }
          })
          .catch((error) => {
            console.error("Error fetching search results", error);
          });
      } else {
        // Clear results if search term is too short
        if (isInDrilldown) {
          this.drilldownCommentSearchResults = [];
        } else {
          this.commentSearchResults = [];
        }
      }
    },
    refetchTeamBreakdown() {
      // Reset tree state when aggregation or filters change
      this.resetTreeState();

      // Set fetching state
      this.isFetchingTableData = true;
      if (this.dxiModalOpen) {
        const selectedItem = dxiDrilldownTabs.find(
          (t) => t.tab == this.selectedDrilldownTab,
        );
        this.fetchTab(selectedItem.tab, selectedItem.url, "dxi");
      } else {
        const selectedItem = drilldownTabs.find(
          (t) => t.tab == this.selectedDrilldownTab,
        );
        if (selectedItem?.url) {
          this.fetchTab(this.selectedDrilldownTab, selectedItem.url, "driver");
        }
      }
    },
    /**
     * Required by teamTreeExpansionMixin - provides the API endpoint for fetching child team data
     * when expanding team rows in the breakdown table. Do not remove this method.
     * @returns {string} The URL endpoint for child data requests
     */
    getChildDataEndpoint() {
      if (this.dxiModalOpen) {
        const teamBreakdownTab = this.visibleDxiDrilldownTabs?.find(
          (t) => t.tab === "team_breakdown",
        );
        return teamBreakdownTab?.url;
      } else {
        const teamBreakdownTab = this.visibleDrilldownTabs?.find(
          (t) => t.tab === "team_breakdown",
        );
        return teamBreakdownTab?.url;
      }
    },

    /**
     * Required by teamTreeExpansionMixin - provides query parameters for fetching child team data
     * when expanding team rows. Do not remove this method.
     * @returns {Object} Parameters object containing filters and context for the API request
     */
    getChildDataParams(parentRow) {
      const currentParams = Object.fromEntries(
        getQueryStringParams().entries(),
      );
      return {
        ...currentParams,
        tag_ids: this.tagIds.join(","),
        factor_id: this.targetedFactor,
        prev_ss: this.$store?.snapshotResultFilters?.previousSnapshotId,
        branch: this.$store?.snapshotResultFilters?.branch,
        aggregation: this.$store.snapshotResultFilters.aggregation,
        parent_squad_id: parentRow ? this.getRowId(parentRow) : undefined,
      };
    },

    /**
     * Required by teamTreeExpansionMixin - provides cached squad data for parent/child lookups
     * and team hierarchy navigation. Do not remove this method.
     * @returns {Array} Array of squad objects with id, parent_id, is_parent properties
     */
    getCachedSquads() {
      return this.cachedSquads || [];
    },

    /**
     * Required by teamTreeExpansionMixin - provides benchmark data to apply to child team rows
     * when they are loaded. Do not remove this method.
     * @returns {Object} Benchmark data with p_50, p_75, p_90 properties
     */
    getBenchmarkData() {
      return {};
    },
    get exportCommentsUrlWithParams() {
      return `${this.exportCommentsUrl}${this.queryParams()}`;
    },

    async flagComment(comment) {
      try {
        await fetch(`/snapshot_response/${comment.id}/flag`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: comment.id }),
        }).then((res) => res.json());

        // Remove comment from collections
        this.allComments = this.allComments.filter((c) => c.id !== comment.id);
        this.commentIds.delete(comment.id);
        if (this.drilldownData.comments) {
          this.drilldownData.comments.data =
            this.drilldownData.comments.data.filter((c) => c.id !== comment.id);
        }
        if (this.commentSearchResults.length > 0) {
          this.commentSearchResults = this.commentSearchResults.filter(
            (c) => c.id !== comment.id,
          );
        }

        // Add the comment to the store's flagged comments
        const currentFlagged = this.$store.flaggedComments || [];
        this.$store.flaggedComments = [...currentFlagged, comment];

        pushNotification({
          title: "Comment flagged",
          desc: "Comment was successfully flagged.",
        });
      } catch (error) {
        console.error("Error flagging comment:", error);
        if (error.status === 403) {
          pushNotification({
            title: "Permission denied",
            desc: "Only admins can quarantine comments.",
            type: "error",
          });
        } else {
          pushNotification({
            title: "Error",
            desc: "Failed to quarantine comment. Please try again.",
            type: "error",
          });
        }
      }
    },
    handleAggregationChange(detail) {
      if (detail.methodName && typeof this[detail.methodName] === "function") {
        this[detail.methodName]();
      }
    },
    handleComparisonChange() {
      this.fetchAll();
      const currentTab = this.selectedDrilldownTab;
      const tabConfig = this.dxiModalOpen
        ? this.visibleDxiDrilldownTabs.find((t) => t.tab === currentTab)
        : this.visibleDrilldownTabs.find((t) => t.tab === currentTab);
      const modalView = this.dxiModalOpen ? "dxi" : "driver";

      if (currentTab && tabConfig?.url) {
        this.fetchTab(currentTab, tabConfig.url, modalView);
      }
    },

    // Get group names for tooltips using mixin resolver
    getGroupNames() {
      return this.resolveGroupNames(this.groups, this.getCurrentGroupIds());
    },
  };
}
